import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import BottomNav from "./components/BottomNav";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Feed from "./pages/Feed";
import Explore from "./pages/Explore";
import Communities from "./pages/Communities";
import Community from "./pages/Community";
import Dance from "./pages/Dance";
import DanceChallenge from "./pages/DanceChallenge";
import Create from "./pages/Create";
import Messages from "./pages/Messages";
import Thread from "./pages/Thread";
import Notifications from "./pages/Notifications";
import Profile from "./pages/Profile";
import PostDetail from "./pages/PostDetail";
import Live from "./pages/Live";
import GoLive from "./pages/GoLive";
import LiveViewer from "./pages/LiveViewer";
import Replay from "./pages/Replay";
import Onboarding from "./pages/Onboarding";
import Verify from "./pages/Verify";
import Saves from "./pages/Saves";
import AdminDashboard from "./pages/AdminDashboard";
import DataSafety from "./pages/DataSafety";
import MusicBrowse from "./pages/MusicBrowse";
import CreatorStudio from "./pages/CreatorStudio";
import CoinShop from "./pages/CoinShop";
import CreatorEarnings from "./pages/CreatorEarnings";
import Wallet from "./pages/Wallet";
import Leaderboard from "./pages/Leaderboard";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Guidelines from "./pages/Guidelines";
import ResetPassword from "./pages/ResetPassword";
import AccountRecovery from "./pages/AccountRecovery";
import RecoverIdentifier from "./pages/RecoverIdentifier";
import Tag from "./pages/Tag";
import Trending from "./pages/Trending";
import Settings from "./pages/Settings";
import VoiceStudio from "./pages/VoiceStudio";
import Rooms from "./pages/Rooms";
import RoomsCreate from "./pages/RoomsCreate";
import RoomView from "./pages/RoomView";
import Analytics from "./pages/Analytics";
import AIVoiceStudio from "./pages/AIVoiceStudio";
import AIVoiceover from "./pages/AIVoiceover";
import GiftShop from "./pages/GiftShop";
import GiftCreator from "./pages/GiftCreator";
import GiftCreatorAgreement from "./pages/GiftCreatorAgreement";
import AIVideoStudio from "./pages/AIVideoStudio";
import AISongStudio from "./pages/AISongStudio";
import CreateHub from "./pages/CreateHub";
import MediaStudio from "./pages/MediaStudio";
import FanClub from "./pages/FanClub";
import CreatorCenter from "./pages/CreatorCenter";
import Backpack from "./pages/Backpack";
import { initSentry } from "./lib/sentry";
import { initFirebase } from "./lib/firebase";
import PushInit from "./lib/pushInit";
import { Toaster } from "sonner";

initSentry();
initFirebase();

function PublicGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-ink flex items-center justify-center text-cream/40 text-xs tracking-[0.3em] uppercase animate-pulse">Loading…</div>;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<PublicGate><Login /></PublicGate>} />
        <Route path="/signup" element={<PublicGate><Signup /></PublicGate>} />
        <Route path="/forgot-password" element={<PublicGate><AccountRecovery /></PublicGate>} />
        <Route path="/recover" element={<PublicGate><AccountRecovery /></PublicGate>} />
        <Route path="/recover-id" element={<PublicGate><RecoverIdentifier /></PublicGate>} />
        <Route path="/reset-password" element={<PublicGate><ResetPassword /></PublicGate>} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/guidelines" element={<Guidelines />} />
        <Route path="/" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
        <Route path="/explore" element={<ProtectedRoute><Explore /></ProtectedRoute>} />
        <Route path="/communities" element={<ProtectedRoute><Communities /></ProtectedRoute>} />
        <Route path="/c/:slug" element={<ProtectedRoute><Community /></ProtectedRoute>} />
        <Route path="/dance" element={<ProtectedRoute><Dance /></ProtectedRoute>} />
        <Route path="/dance/:slug" element={<ProtectedRoute><DanceChallenge /></ProtectedRoute>} />
        <Route path="/create" element={<ProtectedRoute><Create /></ProtectedRoute>} />
        <Route path="/media-studio" element={<ProtectedRoute><MediaStudio /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
        <Route path="/messages/:peerId" element={<ProtectedRoute><Thread /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/u/:username" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/p/:id" element={<ProtectedRoute><PostDetail /></ProtectedRoute>} />
        <Route path="/live" element={<ProtectedRoute><Live /></ProtectedRoute>} />
        <Route path="/live/go" element={<ProtectedRoute><GoLive /></ProtectedRoute>} />
        <Route path="/live/watch/:sessionId" element={<ProtectedRoute><LiveViewer /></ProtectedRoute>} />
        <Route path="/live/replay/:sessionId" element={<ProtectedRoute><Replay /></ProtectedRoute>} />
        <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
        <Route path="/earnings" element={<ProtectedRoute><CreatorEarnings /></ProtectedRoute>} />
        <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
        <Route path="/tag/:tag" element={<ProtectedRoute><Tag /></ProtectedRoute>} />
        <Route path="/trending" element={<ProtectedRoute><Trending /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/welcome" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/verify" element={<ProtectedRoute><Verify /></ProtectedRoute>} />
        <Route path="/saves" element={<ProtectedRoute><Saves /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/data-safety" element={<DataSafety />} />
        <Route path="/music" element={<ProtectedRoute><MusicBrowse /></ProtectedRoute>} />
        <Route path="/studio" element={<ProtectedRoute><CreatorStudio /></ProtectedRoute>} />
        <Route path="/voice" element={<ProtectedRoute><VoiceStudio /></ProtectedRoute>} />
        <Route path="/rooms" element={<ProtectedRoute><Rooms /></ProtectedRoute>} />
        <Route path="/rooms/new" element={<ProtectedRoute><RoomsCreate /></ProtectedRoute>} />
        <Route path="/rooms/:id" element={<ProtectedRoute><RoomView /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
        <Route path="/shop" element={<ProtectedRoute><CoinShop /></ProtectedRoute>} />
        <Route path="/cashout" element={<ProtectedRoute><CreatorEarnings /></ProtectedRoute>} />
        <Route path="/ai-voice" element={<ProtectedRoute><AIVoiceStudio /></ProtectedRoute>} />
        <Route path="/ai-voiceover" element={<ProtectedRoute><AIVoiceover /></ProtectedRoute>} />
        <Route path="/hub" element={<ProtectedRoute><CreateHub /></ProtectedRoute>} />
        <Route path="/ai-video" element={<ProtectedRoute><AIVideoStudio /></ProtectedRoute>} />
        <Route path="/ai-song" element={<ProtectedRoute><AISongStudio /></ProtectedRoute>} />
        <Route path="/gifts" element={<ProtectedRoute><GiftShop /></ProtectedRoute>} />
        <Route path="/gifts/create" element={<ProtectedRoute><GiftCreator /></ProtectedRoute>} />
        <Route path="/fanclub/:creatorId" element={<ProtectedRoute><FanClub /></ProtectedRoute>} />
        <Route path="/creator-center" element={<ProtectedRoute><CreatorCenter /></ProtectedRoute>} />
        <Route path="/backpack" element={<ProtectedRoute><Backpack /></ProtectedRoute>} />
        <Route path="/gift-creator-agreement" element={<GiftCreatorAgreement />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <BottomNav />
      <Toaster theme="dark" position="top-center" />
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <div className="App grain">
      <AuthProvider>
        <PushInit />
        <AppRoutes />
      </AuthProvider>
    </div>
  );
}
